import React, { useState, useEffect, useRef } from 'react';
import { Container, Header, Box, Spinner, StatusIndicator } from '@cloudscape-design/components';
import { ModelResponse } from '../types';
import './ResponseCard.css';

interface ResponseCardProps {
  response: ModelResponse;
}

/**
 * Hook that returns elapsed seconds since mount, ticking every second.
 * Stops ticking when `active` becomes false.
 */
function useElapsedSeconds(active: boolean): number {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!active) return;
    startRef.current = Date.now();
    setElapsed(0);
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  return elapsed;
}

export const ResponseCard: React.FC<ResponseCardProps> = ({ response }) => {
  const { modelName, responseText, status, metrics, errorMessage, isGrok } = response;
  const isWaiting = status === 'streaming' && !responseText;
  const elapsed = useElapsedSeconds(isWaiting);

  const renderContent = () => {
    if (status === 'disabled') {
      return (
        <Box textAlign="center" padding="l">
          <StatusIndicator type="stopped">Model Disabled</StatusIndicator>
        </Box>
      );
    }

    if (status === 'error') {
      return (
        <Box textAlign="center" padding="l">
          <StatusIndicator type="error">
            {errorMessage || 'An error occurred'}
          </StatusIndicator>
        </Box>
      );
    }

    if (status === 'timeout') {
      return (
        <Box textAlign="center" padding="l">
          <StatusIndicator type="warning">
            Model exceeded 5-minute timeout
          </StatusIndicator>
        </Box>
      );
    }

    if (status === 'streaming' && !responseText) {
      const label = response.statusText || 'Waiting for response...';
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
          <Box variant="p" padding={{ top: 's' }}>
            {label}{elapsed > 0 ? ` (${elapsed}s)` : ''}
          </Box>
        </Box>
      );
    }

    return (
      <Box padding="s">
        <div className="response-text">
          {responseText}
          {status === 'streaming' && <span className="cursor">▊</span>}
        </div>
      </Box>
    );
  };

  const renderMetrics = () => {
    if (!metrics || status === 'disabled' || status === 'error') {
      return null;
    }

    return (
      <Box padding={{ top: 's' }} fontSize="body-s" color="text-status-inactive">
        <div className="metrics">
          {metrics.timeToFirstToken !== undefined && (
            <span>First token: {metrics.timeToFirstToken}ms</span>
          )}
          {metrics.totalTokens !== undefined && (
            <span>Tokens: {metrics.totalTokens}</span>
          )}
          {metrics.responseTime !== undefined && (
            <span>Total time: {(metrics.responseTime / 1000).toFixed(2)}s</span>
          )}
        </div>
      </Box>
    );
  };

  return (
    <Container
      className={isGrok ? 'response-card grok-response' : 'response-card'}
      header={
        <Header variant="h3">
          {modelName}
          {status === 'complete' && (
            <Box display="inline" padding={{ left: 's' }}>
              <StatusIndicator type="success">Complete</StatusIndicator>
            </Box>
          )}
          {status === 'streaming' && (
            <Box display="inline" padding={{ left: 's' }}>
              <StatusIndicator type="in-progress">Streaming</StatusIndicator>
            </Box>
          )}
        </Header>
      }
    >
      {renderContent()}
      {renderMetrics()}
    </Container>
  );
};
